if (!customElements.get('dl-page-audio-performance-y')) {
  class DlPageAudioPerformanceY extends HTMLElement {
    constructor() {
      super();
      this.raf = null;
      this.isVisible = true;
      this.syncedVideo = null;
      this.keyframes = null;
      this.copySteps = [];
      this.onScroll = this.requestUpdate.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.onSeeked = this.handleSeeked.bind(this);
      this.onVideoMeta = this.handleVideoMeta.bind(this);
      this.onVideoData = this.handleVideoData.bind(this);
    }

    connectedCallback() {
      this.scrollArea = this.querySelector('.dl-page-audio-performance-y__scroll');
      this.sticky = this.querySelector('.dl-page-audio-performance-y__sticky');
      this.mobileHeader = this.querySelector('.dl-page-audio-performance-y__header--mobile');
      this.videos = Array.from(this.querySelectorAll('.dl-page-audio-performance-y__video'));
      this.hasEndImage = Boolean(this.querySelector('.dl-page-audio-performance-y__end img'));
      this.hasFinalImage = Boolean(this.querySelector('.dl-page-audio-performance-y__final img'));
      this.copySteps = Array.from(this.querySelectorAll('[data-copy-step]'))
        .map((el) => ({
          el,
          appearAt: this.clamp(this.readNumber(el.dataset.appearAt, 0) / 100),
        }))
        .sort((a, b) => a.appearAt - b.appearAt);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.scrollHeightRatio = this.readNumber(this.dataset.scrollSpeed, 100) / 100;
      this.videoFps = this.readNumber(this.dataset.videoFps, 30);
      this.viewportWidth = window.innerWidth;
      this.useCanvasScrub = /Android/i.test(navigator.userAgent);

      this.videos.forEach((video) => {
        video.muted = true;
        video.pause();
        video.preload = 'none';
        if (this.useCanvasScrub) this.setupFrameCanvas(video);
        video.addEventListener('loadedmetadata', this.onVideoMeta);
        video.addEventListener('loadeddata', this.onVideoData);
        video.addEventListener('seeked', this.onSeeked);
      });
      this.syncVideoPreload();
      this.syncMobileSpacing();

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) this.requestUpdate();
        },
        { rootMargin: '100% 0px' }
      );
      this.observer.observe(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.syncScrollLength();
      this.requestUpdate();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.observer?.disconnect();
      this.videos?.forEach((video) => {
        video.removeEventListener('loadedmetadata', this.onVideoMeta);
        video.removeEventListener('loadeddata', this.onVideoData);
        video.removeEventListener('seeked', this.onSeeked);
        if (video.dlFrameRaf) cancelAnimationFrame(video.dlFrameRaf);
      });
      if (this.raf) cancelAnimationFrame(this.raf);
    }

    handleResize() {
      const nextWidth = window.innerWidth;
      const widthChanged = Math.abs(nextWidth - this.viewportWidth) > 1;
      this.viewportWidth = nextWidth;

      // On mobile, showing/hiding the browser chrome emits resize events
      // while the user is scrolling. A height-only resize must not rewrite
      // this section's scroll length or move the document.
      if (widthChanged) {
        this.syncVideoPreload();
        this.syncMobileSpacing();
        this.syncScrollLength();
      }
      this.requestUpdate();
    }

    syncMobileSpacing() {
      if (!this.sticky) return;
      const heightScale = this.sticky.clientHeight / 660;
      this.style.setProperty('--dl-audio-copy-bottom', `${40 * heightScale}px`);
      this.style.setProperty('--dl-audio-copy-gap', `${4 * heightScale}px`);
      this.style.setProperty('--dl-audio-progress-bottom', `${12 * heightScale}px`);
      if (window.matchMedia('(max-width: 990px)').matches && this.mobileHeader) {
        this.style.setProperty(
          '--dl-audio-mobile-intro-height',
          `${Math.ceil(this.mobileHeader.offsetHeight)}px`,
        );
      }
    }

    setupFrameCanvas(video) {
      const canvas = document.createElement('canvas');
      const breakpointClass = video.classList.contains('dl-page-audio-performance-y__asset--mobile')
        ? 'dl-page-audio-performance-y__asset--mobile'
        : 'dl-page-audio-performance-y__asset--desktop';
      canvas.className = `dl-page-audio-performance-y__video-frame ${breakpointClass}`;
      canvas.setAttribute('aria-hidden', 'true');
      video.insertAdjacentElement('afterend', canvas);
      video.dlFrameCanvas = canvas;
    }

    handleVideoMeta(event) {
      if (this.useCanvasScrub) this.drawVideoFrame(event.currentTarget);
      this.syncScrollLength();
    }

    handleVideoData(event) {
      if (this.useCanvasScrub) this.drawVideoFrame(event.currentTarget);
    }

    drawVideoFrame(video) {
      const canvas = video?.dlFrameCanvas;
      if (!canvas || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return false;

      if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
      }

      const context = canvas.getContext('2d', { alpha: false });
      if (!context) return false;

      try {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.classList.add('is-ready');
        return true;
      } catch (_error) {
        return false;
      }
    }

    requestUpdate() {
      if (this.raf || !this.isVisible) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.update();
      });
    }

    syncScrollLength() {
      if (!this.scrollArea || this.reducedMotion.matches) return;

      const video = this.activeVideo();
      const isMobile = window.matchMedia('(max-width: 990px)').matches;
      const viewport = (isMobile ? this.sticky?.clientHeight : window.innerHeight) || 1;
      const duration = video && Number.isFinite(video.duration) ? video.duration : 0;
      const fps = this.videoFps > 0 ? this.videoFps : 30;
      const frames = duration > 0 ? Math.max(1, duration * fps) : 0;

      // Mobile has a normal-flow heading before the sticky scene, so video
      // scrubbing starts as soon as the media reaches and sticks to the top.
      const ratio = Math.max(0.01, this.scrollHeightRatio);
      const introTravel = (isMobile ? 0 : viewport * 0.06) * ratio;
      const outroTravel = viewport * (this.hasFinalImage ? 0.95 : 0.7) * ratio;
      const durationTravel = duration * viewport * 0.85;
      const frameTravel = frames * 18;
      const videoTravel = (
        duration > 0
          ? Math.max(viewport * 0.9, durationTravel, frameTravel)
          : viewport * 1.6
      ) * ratio;
      const totalTravel = introTravel + videoTravel + outroTravel;

      this.keyframes = this.buildKeyframes(introTravel / totalTravel, videoTravel / totalTravel);

      const nextHeight = `${Math.round(totalTravel)}px`;
      if (this.style.getPropertyValue('--dl-audio-scroll-height') === nextHeight && this.syncedVideo === video) {
        return;
      }

      const previousTravel = Math.max(1, this.scrollArea.offsetHeight - viewport);
      const previousRect = this.scrollArea.getBoundingClientRect();
      const previousProgress = this.clamp(-previousRect.top / previousTravel);
      // Mobile browser chrome frequently changes the viewport height while
      // scrolling. Preserve progress only while this sticky scene is active;
      // once it has been passed, scrolling back to its end is disruptive.
      const shouldLockScroll = !isMobile && previousRect.top < 0 && previousRect.bottom > viewport;

      this.syncedVideo = video;
      this.style.setProperty('--dl-audio-scroll-height', nextHeight);

      if (shouldLockScroll) {
        const sectionTop = window.scrollY + previousRect.top;
        window.scrollTo({ top: sectionTop + previousProgress * totalTravel });
      }
    }

    buildKeyframes(introEnd, videoSpan) {
      const videoEnd = introEnd + videoSpan;
      const outroSpan = Math.max(0.001, 1 - videoEnd);

      // Cut between stills and video. Crossfading two PNG/video layers with
      // semi-transparent shadows stacks them and flashes a darker composite.
      return {
        videoIn: [introEnd, introEnd],
        startOut: [introEnd, introEnd],
        videoSeek: [introEnd, videoEnd],
        endIn: [videoEnd, videoEnd],
        videoOut: [videoEnd, videoEnd],
        finalIn: [videoEnd + outroSpan * 0.38, videoEnd + outroSpan * 0.55],
        endOut: [videoEnd + outroSpan * 0.38, videoEnd + outroSpan * 0.55],
      };
    }

    update() {
      if (!this.scrollArea || this.reducedMotion.matches) return;
      if (!this.keyframes) this.syncScrollLength();

      const rect = this.scrollArea.getBoundingClientRect();
      const isMobile = window.matchMedia('(max-width: 990px)').matches;
      const viewport = (isMobile ? this.sticky?.clientHeight : window.innerHeight) || 1;
      const mobileIntroHeight = isMobile ? (this.mobileHeader?.offsetHeight || 0) : 0;
      const sceneTop = rect.top + mobileIntroHeight;
      const travel = Math.max(1, rect.height - viewport - mobileIntroHeight);
      const progress = this.clamp(-sceneTop / travel);
      const video = this.activeVideo();
      const hasVideo = Boolean(video);
      const kf = this.keyframes;

      this.style.setProperty('--dl-audio-progress', progress.toFixed(4));
      this.style.setProperty(
        '--dl-audio-start-opacity',
        hasVideo ? this.fadeOut(progress, ...kf.startOut) : this.fadeOut(progress, 0.28, 0.42)
      );

      const videoOut = this.hasEndImage ? this.fadeOut(progress, ...kf.videoOut) : 1;
      this.style.setProperty(
        '--dl-audio-video-opacity',
        hasVideo ? Math.min(this.fadeIn(progress, ...kf.videoIn), videoOut) : 0
      );

      const endIn = this.hasEndImage ? this.fadeIn(progress, ...kf.endIn) : 0;
      const endOut = this.hasFinalImage ? this.fadeOut(progress, ...kf.endOut) : 1;
      this.style.setProperty('--dl-audio-end-opacity', Math.min(endIn, endOut));
      this.style.setProperty(
        '--dl-audio-final-opacity',
        this.hasFinalImage ? this.fadeIn(progress, ...kf.finalIn) : 0
      );

      this.updateCopySteps(progress);
      this.seekVideo(video, this.range(progress, ...kf.videoSeek));
    }

    updateCopySteps(progress) {
      if (!this.copySteps.length) return;

      this.copySteps.forEach((step, index) => {
        const next = this.copySteps[index + 1];
        const animationStart = step.appearAt;
        const animationEnd = next ? next.appearAt : 1;
        const animationMiddle = animationStart + (animationEnd - animationStart) / 2;
        const travel = 300;
        let opacity;
        let y;

        if (progress <= animationMiddle) {
          const enterProgress = this.smoothStep(
            this.range(progress, animationStart, animationMiddle),
          );
          opacity = enterProgress;
          y = (1 - enterProgress) * travel;
        } else {
          const exitProgress = this.smoothStep(
            this.range(progress, animationMiddle, animationEnd),
          );
          opacity = 1 - exitProgress;
          y = -exitProgress * travel;
        }

        step.el.style.setProperty('--dl-audio-copy-opacity', opacity.toFixed(4));
        step.el.style.setProperty('--dl-audio-copy-y', `${y.toFixed(2)}px`);
      });
    }

    activeVideo() {
      const isMobile = window.matchMedia('(max-width: 990px)').matches;
      const mobile = this.videos.find((video) => video.classList.contains('dl-page-audio-performance-y__asset--mobile'));
      const desktop = this.videos.find((video) => video.classList.contains('dl-page-audio-performance-y__asset--desktop'));
      return isMobile ? mobile || desktop : desktop || mobile;
    }

    syncVideoPreload() {
      const active = this.activeVideo();
      this.videos.forEach((video) => {
        this.stashVideoSources(video);
        if (video === active) this.enableVideoSource(video);
        else this.disableVideoSource(video);
      });
    }

    stashVideoSources(video) {
      if (video.dataset.srcStashed) return;
      if (video.getAttribute('src')) video.dataset.src = video.getAttribute('src');
      video.querySelectorAll('source').forEach((source) => {
        const src = source.getAttribute('src');
        if (src) source.dataset.src = src;
      });
      video.dataset.srcStashed = 'true';
    }

    enableVideoSource(video) {
      let changed = video.preload !== 'auto';
      if (video.dataset.src && video.getAttribute('src') !== video.dataset.src) {
        video.setAttribute('src', video.dataset.src);
        changed = true;
      }
      video.querySelectorAll('source').forEach((source) => {
        if (source.dataset.src && source.getAttribute('src') !== source.dataset.src) {
          source.setAttribute('src', source.dataset.src);
          changed = true;
        }
      });
      video.preload = 'auto';
      if (changed || video.readyState < 1) video.load();
    }

    disableVideoSource(video) {
      let changed = false;
      if (video.getAttribute('src')) {
        video.removeAttribute('src');
        changed = true;
      }
      video.querySelectorAll('source').forEach((source) => {
        if (source.getAttribute('src')) {
          source.removeAttribute('src');
          changed = true;
        }
      });
      video.preload = 'none';
      video.pause();
      if (changed) video.load();
    }

    seekVideo(video, progress) {
      if (!video || video.readyState < 1 || !Number.isFinite(video.duration) || video.duration <= 0) return;
      const frameDuration = 1 / Math.max(1, this.videoFps);
      // Avoid seeking into the decoder's empty last sample, which flashes white.
      const lastFrameTime = Math.max(0, video.duration - frameDuration * 2);
      video.dlTargetTime = lastFrameTime * progress;
      this.flushVideoSeek(video);
    }

    handleSeeked(event) {
      const video = event.currentTarget;
      if (!this.useCanvasScrub) {
        this.flushVideoSeek(video);
        return;
      }

      if (video.dlFrameRaf) cancelAnimationFrame(video.dlFrameRaf);
      video.dlFrameRaf = requestAnimationFrame(() => {
        video.dlFrameRaf = null;
        this.drawVideoFrame(video);
        this.flushVideoSeek(video);
      });
    }

    flushVideoSeek(video) {
      if (video.seeking || !Number.isFinite(video.dlTargetTime)) return;

      // Do not cancel an in-flight decode. Apply only the newest queued frame
      // after the browser has presented the previous seek result.
      const seekThreshold = this.useCanvasScrub ? 1 / Math.min(15, this.videoFps) : 1 / 30;
      if (Math.abs(video.currentTime - video.dlTargetTime) > seekThreshold) {
        video.currentTime = video.dlTargetTime;
      }
    }

    fadeIn(value, start, end) {
      return this.smoothStep(this.range(value, start, end));
    }

    fadeOut(value, start, end) {
      return 1 - this.smoothStep(this.range(value, start, end));
    }

    range(value, start, end) {
      if (end === start) return value >= start ? 1 : 0;
      return this.clamp((value - start) / (end - start));
    }

    clamp(value) {
      return Math.min(1, Math.max(0, value));
    }

    smoothStep(value) {
      return value * value * (3 - 2 * value);
    }

    readNumber(value, fallback) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    }
  }

  customElements.define('dl-page-audio-performance-y', DlPageAudioPerformanceY);
}
